import { Spacing } from '@persona-id/design-tokens';
import { SearchIcon } from '@persona-id/design-tokens/icons';
import IconPlus from '@persona-id/design-tokens/icons/PlusIcon';
import { DateTime } from 'luxon';
import { useState } from 'react';
import { Route, useNavigate } from 'react-router-dom-v5-compat';
import styled from 'styled-components';

import ContextMenu from 'dashboard/components/ContextMenu';
import { Item } from 'dashboard/components/ContextMenu/ContextMenu';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import { OptionType } from 'dashboard/components/ReactSelect';
import { SentryRoutes } from 'dashboard/components/SentryRoute/SentryRoute';
import { RoutedTabbedSection } from 'dashboard/components/TabbedSectionV2';
import MultiSelect, { useLabelTextMapper } from 'dashboard/components-lib/MultiSelect';
import FormCloneImporter, {
  FormCloneImporterData,
} from 'dashboard/forms/FormCloneImporter/FormCloneImporter';
import {
  AddDataSourceResponse,
  FormNewDataSourceData,
  FormNewDataSourceDataS3,
} from 'dashboard/forms/FormNewDataSource/types';
import { FormNewImporterData } from 'dashboard/forms/FormNewImporter';
import { Container, Header, Section } from 'dashboard/layouts/IndexViewLayoutV2';
import { ModalBody, ModalContainer, ModalHeader } from 'dashboard/layouts/ModalLayout';
import TableLayout, {
  TableLinkTR,
  TableNullState,
  TableTD,
  TableTH,
} from 'dashboard/layouts/TableLayout';
import { DATA_SOURCE_TYPES } from 'dashboard/lib/constants/data-sources';
import { titleCase } from 'dashboard/lib/utils/stringFormat';
import {
  DataSourceStatus,
  DataSourceUniqueStatus,
  ExternalIntegrationDataSource,
  ExternalIntegrationStatus,
  GoogleSheetsFilesResponse,
  S3Config,
  S3Object,
} from 'dashboard/models/external-integration';
import {
  Importer,
  IMPORTER_STATUS_TO_LABEL,
  IMPORTER_STATUS_TO_VARIANT,
  IMPORTER_TYPES_TO_DATA_SOURCE_TYPES,
  ImporterStatus,
} from 'dashboard/models/importer';
import ImporterTypeIcon from 'dashboard/views/DashboardImporterStepperView/ImporterTypeIcon';
import AddDataSourceModal from 'dashboard/views/DashboardImportersView/AddDataSourceModal';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Input, { InputGroup } from 'design-system/components/Input';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import Sticker from 'design-system/components/Sticker';
import { Variant } from 'design-system/theme';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useToggle from 'lib/hooks/useToggle';
import { ApiModel } from 'lib/interfaces/api';

const StyledTableTD = styled(TableTD)`
  padding-top: 6px;
  padding-bottom: 6px;
`;

const FiltersRow = styled('div')`
  display: grid;
  column-gap: ${Spacing[4]};
  grid-template-columns: 3fr 1fr 1fr;
  align-items: end;
  margin-bottom: 10px;
`;

const IMPORTER_STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: ImporterStatus.Active,
  },
  {
    label: 'Inactive',
    value: ImporterStatus.Inactive,
  },
  {
    label: 'Archived',
    value: ImporterStatus.Archived,
  },
];

const DATA_SOURCES_STATUS_FILTER_OPTIONS = [
  {
    label: 'Active',
    value: ExternalIntegrationStatus.Active,
  },
  {
    label: 'Processing',
    value: DataSourceUniqueStatus.Processing,
  },
  {
    label: 'Unhealthy',
    value: ExternalIntegrationStatus.Unhealthy,
  },
  {
    label: 'Inactive',
    value: ExternalIntegrationStatus.Inactive,
  },
  {
    label: 'Archived',
    value: ExternalIntegrationStatus.Archived,
  },
];

const SORT_OPTIONS = [
  {
    label: 'Created new to old',
    value: '-created_at',
  },
  {
    label: 'Created old to new',
    value: 'created_at',
  },
  {
    label: 'Name A to Z',
    value: 'name',
  },
  {
    label: 'Name Z to A',
    value: '-name',
  },
];

const StatusSticker = ({ status }: { status: DataSourceStatus }) => {
  let variant: Variant = 'basic';
  switch (status) {
    case ExternalIntegrationStatus.Active:
      variant = 'success';
      break;
    case ExternalIntegrationStatus.Archived:
      variant = 'danger';
      break;
    case DataSourceUniqueStatus.Processing:
      variant = 'caution';
  }

  return <Sticker variant={variant}>{titleCase(status)}</Sticker>;
};

interface FiltersComponentProps<T extends string> {
  statusFilterOptions: { label: string; value: T }[];
  filterByValue: T[];
  setFilterByValue: (event: T[]) => void;
  searchValue: string;
  setSearchValue: (event: string) => void;
  sortByValue: string;
  setSortByValue: (event: string) => void;
}

const FiltersComponent = <T extends string>({
  statusFilterOptions,
  filterByValue,
  setFilterByValue,
  searchValue,
  setSearchValue,
  sortByValue,
  setSortByValue,
}: FiltersComponentProps<T>) => {
  const statusFilterLabelTextMapper = useLabelTextMapper(statusFilterOptions, 'Statuses', {
    emptyIsAll: true,
  });

  return (
    <FiltersRow>
      <div>
        <FormControlLabel htmlFor='importers-view-configuration-bar__filter-omni'>
          Search
        </FormControlLabel>
        <InputGroup leftIcon={<SearchIcon />}>
          <Input
            id='importers-view-configuration-bar__filter-omni'
            placeholder='Search'
            size='md'
            value={searchValue}
            onChange={(event) => {
              setSearchValue(event.target.value);
            }}
          />
        </InputGroup>
      </div>
      <div>
        <FormControlLabel htmlFor='importers-view-configuration-bar__select-trigger'>
          Filter By
        </FormControlLabel>
        <MultiSelect
          data-test='importers-view-configuration-bar__select--status'
          labelTextMapper={statusFilterLabelTextMapper}
          selectOptionValues={statusFilterOptions}
          size='md'
          value={filterByValue}
          onChange={setFilterByValue}
        />
      </div>
      <div>
        <FormControlLabel htmlFor='importers-view-configuration-bar__select--sort'>
          Sort By
        </FormControlLabel>
        <Select
          id='importers-view-configuration-bar__select--sort'
          value={sortByValue}
          onValueChange={setSortByValue}
        >
          {SORT_OPTIONS.map((option, i) => (
            <Option key={i} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
      </div>
    </FiltersRow>
  );
};

export interface DashboardImportersViewProps {
  addDataSource: (values: FormNewDataSourceData) => Promise<AddDataSourceResponse>;
  archiveImporter: (importer: Importer) => Promise<void>;
  addImporter: (values: FormNewImporterData) => Promise<void>;
  archiveDataSource: (dataSource: ExternalIntegrationDataSource) => Promise<void>;
  cloneImporter: (importer: Importer, name: string, dataSourceId: string) => Promise<void>;
  refreshDataSource: (dataSource: ExternalIntegrationDataSource) => Promise<void>;
  dataSources: ExternalIntegrationDataSource[];
  getAllGoogleSheets: (code: string, redirectUrl: string) => Promise<GoogleSheetsFilesResponse>;
  importers: Importer[];
  s3Config: S3Config;
  handleListS3Objects: (values: FormNewDataSourceDataS3) => Promise<S3Object[]>;
}

const DashboardImportersView = ({
  addDataSource,
  archiveDataSource,
  archiveImporter,
  cloneImporter,
  refreshDataSource,
  dataSources,
  importers,
  s3Config,
  getAllGoogleSheets,
  handleListS3Objects,
}: DashboardImportersViewProps) => {
  const navigate = useNavigate();

  const [searchValue, setSearchValue] = useState<string>('');
  const [importerFilterByValue, setImporterFilterByValue] = useState<ImporterStatus[]>([
    ImporterStatus.Active,
  ]);
  const [dataSourceFilterByValue, setDataSourceFilterByValue] = useState<DataSourceStatus[]>([
    ExternalIntegrationStatus.Active,
    DataSourceUniqueStatus.Processing,
    ExternalIntegrationStatus.Unhealthy,
  ]);
  const [sortByValue, setSortByValue] = useState<string>('-created_at');

  const handleAddNewImporterClick = () => {
    navigate('/dashboard/imports/importers/new');
  };

  const [addDataSourceModal, { enable: showAddDataSourceModal, disable: hideAddDataSourceModal }] =
    useToggle(false);

  const handleSubmitAddDataSource = async (values: FormNewDataSourceData) => {
    const response = await addDataSource(values);

    if (!response || typeof response !== 'string') {
      hideAddDataSourceModal();
    }
    return response;
  };

  const compareCreatedAt = (
    a: Importer | ExternalIntegrationDataSource,
    b: Importer | ExternalIntegrationDataSource,
  ) => {
    return (
      DateTime.fromISO(a.attributes['created-at']).valueOf() -
      DateTime.fromISO(b.attributes['created-at']).valueOf()
    );
  };

  const [cloneModal, { enable: showCloneModal, disable: hideCloneModal }] = useToggle(false);

  const [importerToBeCloned, setImporterToBeCloned] = useState<Importer | null>(null);

  const dataSourceOptions: OptionType[] = importerToBeCloned
    ? dataSources
        .sort((a, b) => compareCreatedAt(b, a))
        .filter(
          (dataSource) =>
            IMPORTER_TYPES_TO_DATA_SOURCE_TYPES[importerToBeCloned.type].includes(
              dataSource.type,
            ) && dataSource.attributes.status !== ExternalIntegrationStatus.Archived,
        )
        .map((dataSource) => {
          return { label: dataSource.attributes.name, value: dataSource.id };
        })
    : [];

  const oldDataSource = dataSources.find(
    (d) => d.id === importerToBeCloned?.relationships['external-integration'].data.id,
  );

  const handleCloneModal = async (values: FormCloneImporterData) => {
    if (importerToBeCloned) {
      cloneImporter(importerToBeCloned, values.name, values.dataSourceId);
    }
    hideCloneModal();
  };

  const sortingFunction = (
    a: Importer | ExternalIntegrationDataSource,
    b: Importer | ExternalIntegrationDataSource,
  ) => {
    switch (sortByValue) {
      case 'name':
        return a.attributes.name.localeCompare(b.attributes.name);
      case '-name':
        return b.attributes.name.localeCompare(a.attributes.name);
      case 'created_at':
        return compareCreatedAt(a, b);
      default:
        return compareCreatedAt(b, a);
    }
  };

  const dataSourcesTab = (
    <Card>
      <Card.Body>
        <Card.Title>Data sources</Card.Title>
        <FiltersComponent<DataSourceStatus>
          statusFilterOptions={DATA_SOURCES_STATUS_FILTER_OPTIONS}
          filterByValue={dataSourceFilterByValue}
          setFilterByValue={setDataSourceFilterByValue}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          sortByValue={sortByValue}
          setSortByValue={setSortByValue}
        />
        <TableLayout
          isEmpty={dataSources.length === 0}
          nullState={
            <TableNullState>
              <Spacer height={3} />
              <div>Create data sources to import into Persona.</div>
              <Spacer height={3} />
            </TableNullState>
          }
          headerTR={
            <>
              <TableTH>Name</TableTH>
              <TableTH>Created at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})</TableTH>
              <TableTH>Type</TableTH>
              <TableTH>Status</TableTH>
              <TableTH></TableTH>
            </>
          }
        >
          {dataSources
            .filter((dataSource: ExternalIntegrationDataSource) => {
              const searchValueFilter =
                !searchValue ||
                dataSource.attributes.name.toLowerCase().includes(searchValue.toLowerCase());

              const statusFilter =
                !dataSourceFilterByValue.length ||
                dataSourceFilterByValue.includes(dataSource.attributes.status);

              return searchValueFilter && statusFilter;
            })
            .sort(sortingFunction)
            .map((dataSource) => {
              const displayType =
                dataSource.type in DATA_SOURCE_TYPES
                  ? titleCase(DATA_SOURCE_TYPES[dataSource.type as keyof typeof DATA_SOURCE_TYPES])
                  : dataSource.type;

              const canRefresh = dataSource.type !== ApiModel.ExternalIntegrationDataSourceCsv;

              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              const items: Item[] = [
                canRefresh && {
                  id: 'refresh',
                  onClick: () => refreshDataSource(dataSource),
                  label: 'Refresh',
                },
                {
                  id: 'archive',
                  onClick: () => archiveDataSource(dataSource),
                  hidden: dataSource.attributes.status === ExternalIntegrationStatus.Archived,
                  label: 'Archive',
                  variant: 'danger',
                } as Item,
              ].filter(Boolean);

              return (
                <tr key={dataSource.id}>
                  <StyledTableTD>{dataSource.attributes.name}</StyledTableTD>
                  <StyledTableTD>
                    {DateTime.fromISO(dataSource.attributes['created-at']).toFormat(
                      DATE_AND_TIME_FORMAT,
                    ) +
                      ' ' +
                      DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)}
                  </StyledTableTD>
                  <StyledTableTD>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <ImporterTypeIcon type={displayType || ''} size='18px' />
                      {displayType}
                    </div>
                  </StyledTableTD>
                  <StyledTableTD>
                    <StatusSticker status={dataSource.attributes.status} />
                  </StyledTableTD>
                  <StyledTableTD>
                    <ContextMenu
                      variant='inverse'
                      items={items}
                      renderItem={(item) => (
                        <ContextMenu.Item
                          key={item.id}
                          onClick={(e) => {
                            // Needed to avoid triggering row click below
                            e.stopPropagation();
                            e.preventDefault();
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            item.onClick();
                          }}
                          variant={item.variant}
                        >
                          {item.label}
                        </ContextMenu.Item>
                      )}
                    />
                  </StyledTableTD>
                </tr>
              );
            })}
        </TableLayout>
      </Card.Body>
    </Card>
  );

  const importersTab = (
    <Card>
      <Card.Body>
        <Card.Title>Importers</Card.Title>
        <FiltersComponent<ImporterStatus>
          statusFilterOptions={IMPORTER_STATUS_FILTER_OPTIONS}
          filterByValue={importerFilterByValue}
          setFilterByValue={setImporterFilterByValue}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          sortByValue={sortByValue}
          setSortByValue={setSortByValue}
        />
        <TableLayout
          isEmpty={importers.length === 0}
          nullState={
            <TableNullState>
              <Spacer height={3} />
              <div>Create importers to load data from data sources into Persona.</div>
              <Spacer height={3} />
            </TableNullState>
          }
          headerTR={
            <>
              <TableTH>Name</TableTH>
              <TableTH>Data source</TableTH>
              <TableTH>Created at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})</TableTH>
              <TableTH>
                Last ran at ({DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})
              </TableTH>
              <TableTH>Status</TableTH>
              <TableTH></TableTH>
            </>
          }
        >
          {importers
            .filter((importer: Importer) => {
              const dataSource = dataSources.find(
                (d) => d.id === importer.relationships['external-integration'].data.id,
              );
              const searchValueFilter =
                !searchValue ||
                importer.attributes.name.toLowerCase().includes(searchValue.toLowerCase()) ||
                dataSource?.attributes.name.toLowerCase().includes(searchValue.toLowerCase());

              const statusFilter =
                !importerFilterByValue.length ||
                importerFilterByValue.includes(importer.attributes.status);

              return searchValueFilter && statusFilter;
            })
            .sort(sortingFunction)
            .map((importer) => {
              const dataSource = dataSources.find(
                (d) => d.id === importer.relationships['external-integration'].data.id,
              );
              return (
                <TableLinkTR to={`/dashboard/imports/importers/${importer.id}`} key={importer.id}>
                  <StyledTableTD>{importer.attributes.name}</StyledTableTD>
                  <StyledTableTD>{dataSource?.attributes.name}</StyledTableTD>
                  <StyledTableTD>
                    {DateTime.fromISO(importer.attributes['created-at']).toFormat(
                      DATE_AND_TIME_FORMAT,
                    ) +
                      ' ' +
                      DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)}
                  </StyledTableTD>
                  <StyledTableTD>
                    {importer.attributes['last-synced-at']
                      ? DateTime.fromISO(importer.attributes['last-synced-at']).toFormat(
                          DATE_AND_TIME_FORMAT,
                        ) +
                        ' ' +
                        DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)
                      : 'No completed syncs'}
                  </StyledTableTD>
                  <StyledTableTD>
                    <Sticker variant={IMPORTER_STATUS_TO_VARIANT[importer.attributes.status]}>
                      {IMPORTER_STATUS_TO_LABEL[importer.attributes.status]}
                    </Sticker>
                  </StyledTableTD>
                  <StyledTableTD>
                    <ContextMenu
                      variant='inverse'
                      items={[
                        {
                          id: 'clone',
                          onClick: () => {
                            setImporterToBeCloned(importer);
                            showCloneModal();
                          },
                          label: 'Clone',
                        } as Item,
                        {
                          id: 'archive',
                          onClick: () => archiveImporter(importer),
                          hidden: importer.attributes['archived-at'] != null,
                          label: 'Archive',
                          variant: 'danger',
                        },
                      ]}
                      renderItem={(item) => (
                        <ContextMenu.Item
                          id={`importer-table-row-context-menu-item_${importer.id}`}
                          key={item.id}
                          onClick={(e) => {
                            // Needed to avoid triggering row click below
                            e.stopPropagation();
                            e.preventDefault();
                            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                            item.onClick();
                          }}
                          variant={item.variant}
                        >
                          {item.label}
                        </ContextMenu.Item>
                      )}
                    />
                  </StyledTableTD>
                </TableLinkTR>
              );
            })}
        </TableLayout>
      </Card.Body>
    </Card>
  );

  return (
    <>
      <ModalWrapper isOpen={addDataSourceModal} handleCloseModal={hideAddDataSourceModal}>
        <AddDataSourceModal
          handleClose={hideAddDataSourceModal}
          handleSubmitAddDataSource={handleSubmitAddDataSource}
          getAllGoogleSheets={getAllGoogleSheets}
          s3Config={s3Config}
          handleListS3Objects={handleListS3Objects}
        />
      </ModalWrapper>
      <ModalWrapper isOpen={cloneModal} handleCloseModal={hideCloneModal}>
        <ModalContainer>
          <ModalHeader onClose={hideCloneModal}>Clone Importer</ModalHeader>
          <ModalBody>
            <FormCloneImporter
              handleClose={hideCloneModal}
              onSubmit={handleCloneModal}
              oldName={importerToBeCloned?.attributes.name || ''}
              dataSourceOptions={dataSourceOptions}
              oldDataSource={oldDataSource}
              dataSources={dataSources}
            />
          </ModalBody>
        </ModalContainer>
      </ModalWrapper>
      <Container>
        <Header title='Imports'>
          <Button
            variant='basic'
            onClick={showAddDataSourceModal}
            width='auto'
            prefixIcon={<IconPlus />}
          >
            Add new data source
          </Button>

          <Button
            variant='basic'
            onClick={handleAddNewImporterClick}
            width='auto'
            prefixIcon={<IconPlus />}
          >
            Add new importer
          </Button>
        </Header>
        <Section>
          <SentryRoutes>
            <Route
              path='*'
              element={
                <RoutedTabbedSection
                  tabs={[
                    {
                      name: 'Data Sources',
                      path: 'data-sources',
                      contents: dataSourcesTab,
                    },
                    {
                      name: 'Importers',
                      path: 'importers',
                      contents: importersTab,
                    },
                  ]}
                />
              }
            />
          </SentryRoutes>
        </Section>
      </Container>
    </>
  );
};

export default DashboardImportersView;
